import { Rectangle } from 'pixi.js';
import type { Pixels } from '../../../../types';
import { SeekMode } from '../../../../enums';
import { WordElementMetrics } from '../metrics/WordElementMetrics';

export interface CanvasRendererSettings {
    maxTextWidth: Pixels;
    maxTextHeight: Pixels;
    showActiveBounds: boolean;
    showMaxBounds: boolean;
}

export type AnimatableTextProperties = (typeof CanvasRenderer.animatableProperties)[number];
export enum AnimatableNonGeometricTextProperties {
    scaleX = 'scaleX',
    scaleY = 'scaleY',
}

/**
 * CanvasRenderer is a base class for all canvas renderers. It provides a number of common properties and methods for
 * things such as canvas and transformation. It is responsible for building up state for drawing canvas elements and
 * then drawing them.
 *
 * In context of Luminary, the canvas renderer is expected to cover the entire scene. The text is placed within the
 * scene or canvas through the use of our translation and anchor properties which get passed down from our Luminary
 * asset. No other transformation properties should be passed down from the Luminary asset. Any other transformation
 * animation should be handled by the canvas renderer itself.
 */
export abstract class CanvasRenderer<
    Settings extends CanvasRendererSettings = CanvasRendererSettings,
> {
    /* the x translation of the text (without graphics) expressed in pixels */
    translateX: Pixels = 0 as Pixels;
    /* the y translation of the text (without graphics) expressed in pixels */
    translateY: Pixels = 0 as Pixels;
    /* the x anchor of the text (without graphics) expressed as a normalized value */
    anchorX: number = 0;
    /* the y anchor of the text (without graphics) expressed as a normalized value */
    anchorY: number = 0;
    /* the x scale of the text (container-level scale from PIXI) */
    scaleX: number = 1;
    /* the y scale of the text (container-level scale from PIXI) */
    scaleY: number = 1;

    /* the maximum width of the draw elements expressed in pixels */
    maxTextWidth = 200 as Pixels;
    /* the maximum height of the draw elements expressed in pixels */
    maxTextHeight = 200 as Pixels;

    /* Show the bounds of the text elements actively being draw for visual debugging. */
    showActiveTextBounds = false;
    /* Shows the bounds of the maximum text dimensions for visual debugging. */
    showMaxBounds = false;

    #canvas: HTMLCanvasElement | null = null; // sharedCanvas;
    #context: CanvasRenderingContext2D | null = null;

    #currentTime: number = -1;
    seekMode: SeekMode = SeekMode.ResolveWhenCanPlay;

    get canvas(): HTMLCanvasElement | null {
        return this.#canvas;
    }

    /**
     * The drawing canvas, guaranteed to exist. Throws if accessed before setCanvas.
     * Use in code paths that only run after the canvas has been set (e.g. draw).
     */
    get #drawingCanvas(): HTMLCanvasElement {
        if (this.#canvas === null) {
            throw new Error('TextRenderer canvas accessed before setCanvas');
        }
        return this.#canvas;
    }

    get context(): CanvasRenderingContext2D {
        if (this.#context === null) {
            throw new Error('TextRenderer context accessed before setCanvas');
        }
        return this.#context;
    }

    get canvasWidth(): Pixels {
        return this.#drawingCanvas.width as Pixels;
    }

    get canvasHeight(): Pixels {
        return this.#drawingCanvas.height as Pixels;
    }

    get currentTime(): number {
        return this.#currentTime;
    }

    set currentTime(time: number) {
        this.#currentTime = time;
        this._onSetCurrentTime();
    }

    /** Method which gets called when the current time changes. */
    protected abstract _onSetCurrentTime(): void;

    /** Returns true if the renderer has any text to render. */
    abstract get hasText(): boolean;

    /**
     * Returns true if we have the necessary elements to render to the canvas. In the context of text these are valid,
     * active textMetrics
     */
    abstract get canRender(): boolean;

    apply(settings: Settings) {
        this.maxTextWidth = settings.maxTextWidth;
        this.maxTextHeight = settings.maxTextHeight;
        this.showActiveTextBounds = settings.showActiveBounds;
        this.showMaxBounds = settings.showMaxBounds;
    }

    static #isTextRenderingSupported: boolean | null = null;

    /**
     * Returns true if text rendering is supported in the current environment.
     * Requires a 2d canvas context with the measureText API.
     */
    static get isTextRenderingSupported(): boolean {
        if (CanvasRenderer.#isTextRenderingSupported === null) {
            try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                CanvasRenderer.#isTextRenderingSupported =
                    ctx != null && typeof ctx.measureText === 'function';
            } catch {
                CanvasRenderer.#isTextRenderingSupported = false;
            }
        }
        return CanvasRenderer.#isTextRenderingSupported;
    }

    setCanvas(canvas: HTMLCanvasElement) {
        this.#canvas = canvas;
        this.#context = canvas.getContext('2d')!;
    }

    /** Build up state for drawing canvas elements */
    abstract build(): void;

    abstract resetBuild(): void;

    /** Draw any canvas elements by consuming state produced during build */
    draw() {
        if (!this.#canvas) {
            return;
        }
        this._resetCanvas();
        this.#drawMaxBounds();
        this.#drawActiveBounds();
        this._applyActiveBoundsTransformation();

        // draw elements here

        // WARNING: we don't want to reset the transformation here in case we still want to draw some elements. This is
        // something that happens several times in our tests
    }

    /* Reset the canvas by removing its content and resetting the transformation matrix  */
    _resetCanvas() {
        this.#resetTransformation();
        this.#clearCanvas();
    }

    /* Reset the transformation matrix to the identity matrix */
    #resetTransformation() {
        this.context.resetTransform();
    }

    /* Clear the canvas */
    #clearCanvas() {
        this.context.clearRect(0, 0, this.#drawingCanvas.width, this.#drawingCanvas.height);
    }

    #drawMaxBounds() {
        if (!this.showMaxBounds) {
            return;
        }

        drawRect(this.context, this.maxTextBounds, 'rgba(0, 0, 255, 0.25)');
    }

    #drawActiveBounds() {
        if (!this.showActiveTextBounds || this.activeTextBounds === null) {
            return;
        }
        this.context.save();

        this._applyActiveBoundsTransformation();

        drawRect(this.context, this.activeTextBounds, 'rgba(255, 0, 0, 0.25)');

        this.context.restore();
    }

    /* Set the canvas transformation matrix based on the active bounds and global transformation */
    _applyActiveBoundsTransformation() {
        // apply the translation anchor (based on the active bounds)
        const canvasAnchorX = -(this.anchorX * this.activeTextBounds!.width);
        const canvasAnchorY = -(this.anchorY * this.activeTextBounds!.height);
        this.context.translate(canvasAnchorX, canvasAnchorY);
        this.context.translate(this.translateX, this.translateY);
        // apply container-level scale around the anchor point
        if (this.scaleX !== 1 || this.scaleY !== 1) {
            this.context.scale(this.scaleX, this.scaleY);
        }
        this.context.translate(-this.activeTextBounds!.x, -this.activeTextBounds!.y);
        this.context.translate(0, this.activeAscentStabilizer);
    }

    /* The value which stabilizes the baseline */
    get activeAscentStabilizer(): number {
        return 0;
    }

    /* The bounds of the canvas elements that are currently being drawn */
    abstract get activeTextBounds(): Rectangle | null;

    /* The total bounds of all canvas elements that can be drawn */
    abstract get totalBounds(): Rectangle;

    /* The list of properties that can be animated during draw */
    static get animatableProperties() {
        return [
            ...WordElementMetrics.animatableProperties,
            ...Object.values(AnimatableNonGeometricTextProperties),
        ];
    }

    static get animatableGeometricProperties() {
        return WordElementMetrics.animatableGeometricProperties;
    }

    /**
     * Returns the max bounds of the text area. This does not include any graphics or decorations
     * that could be drawn beyond the text area.
     */
    get maxTextBounds(): Rectangle {
        const x = this.translateX - this.anchorX * this.maxTextWidth;
        const y = this.translateY - this.anchorY * this.maxTextHeight;
        return new Rectangle(x, y, this.maxTextWidth, this.maxTextHeight);
    }

    destroy() {
        this.resetBuild();
    }
}

const drawRect = (context: CanvasRenderingContext2D, coordinates: Rectangle, fill: string) => {
    const { x, y, width, height } = coordinates;
    context.fillStyle = fill;
    context.fillRect(x, y, width, height);
};
